// Initialize Protocol Nomenclatures
// Run this once to populate all default nomenclature data

import { 
  protocolCategoryService,
  protocolTypeService,
  roomTypeService,
  ppeTypeService,
  verificationMethodService,
  triggerEventService,
  frequencyTypeService,
  locationTypeService,
  processCategoryService
} from '../services/nomenclatureService';

async function initializeNomenclatures(userId = 'system-init') {
  console.log('🚀 Starting nomenclature initialization...');

  try {
    // Check if data already exists
    console.log('🔍 Checking for existing data...');
    const existingCategories = await protocolCategoryService.getAll();
    
    let didClear = false;
    if (existingCategories.length > 0) {
      const confirmed = window.confirm(
        `⚠️ Nomenclatures already exist (${existingCategories.length} protocol categories found).\n\n` +
        'Do you want to CLEAR ALL existing data and reinitialize?\n\n' +
        'Click OK to DELETE and reinitialize, or Cancel to keep existing data (protocol types will still be added if missing).'
      );
      
      if (!confirmed) {
        // Don't clear; but seed protocol types if collection is empty
        const existingProtocolTypes = await protocolTypeService.getAll();
        if (existingProtocolTypes.length === 0) {
          console.log('📋 Seeding protocol types only (no clear)...');
          const protocolTypesToSeed = [
            { code: 'HYG-HAND', name: 'Hand Hygiene', description: 'Hand hygiene and sanitization', isActive: true, displayOrder: 1, categoryCode: 'HYG' },
            { code: 'DI-LINE', name: 'Central Line Insertion', description: 'Central venous catheter insertion', isActive: true, displayOrder: 1, categoryCode: 'DI' },
            { code: 'DI-UC', name: 'Urinary Catheter Insertion', description: 'Urinary catheter insertion and care', isActive: true, displayOrder: 2, categoryCode: 'DI' },
            { code: 'DM-MAINT', name: 'Device Maintenance', description: 'Medical device maintenance and flushing', isActive: true, displayOrder: 1, categoryCode: 'DM' },
            { code: 'EC-ENV', name: 'Environmental Cleaning', description: 'Routine environmental cleaning and disinfection', isActive: true, displayOrder: 1, categoryCode: 'EC' },
            { code: 'EC-TERMINAL', name: 'Terminal Cleaning', description: 'Terminal/transfer cleaning', isActive: true, displayOrder: 2, categoryCode: 'EC' },
            { code: 'EC-WASTE', name: 'Waste Management', description: 'Waste segregation and disposal', isActive: true, displayOrder: 3, categoryCode: 'EC' },
            { code: 'PC-ADL', name: 'Activities of Daily Living', description: 'Patient ADL and mobility', isActive: true, displayOrder: 1, categoryCode: 'PC' },
            { code: 'PC-PPE', name: 'PPE Donning/Doffing', description: 'Personal protective equipment use', isActive: true, displayOrder: 2, categoryCode: 'PC' },
            { code: 'MA-IV', name: 'IV Medication', description: 'Intravenous medication administration', isActive: true, displayOrder: 1, categoryCode: 'MA' },
            { code: 'MA-ORAL', name: 'Oral Medication', description: 'Oral medication administration', isActive: true, displayOrder: 2, categoryCode: 'MA' },
            { code: 'SRG-PREOP', name: 'Preoperative', description: 'Preoperative preparation', isActive: true, displayOrder: 1, categoryCode: 'SRG' },
            { code: 'SRG-ASEPSIS', name: 'Surgical Asepsis', description: 'Surgical aseptic technique', isActive: true, displayOrder: 2, categoryCode: 'SRG' },
            { code: 'ISO-CONTACT', name: 'Contact Precautions', description: 'Contact precaution isolation', isActive: true, displayOrder: 1, categoryCode: 'ISO' },
            { code: 'ISO-DROPLET', name: 'Droplet Precautions', description: 'Droplet precaution isolation', isActive: true, displayOrder: 2, categoryCode: 'ISO' },
            { code: 'ISO-AIRBORNE', name: 'Airborne Precautions', description: 'Airborne precaution isolation', isActive: true, displayOrder: 3, categoryCode: 'ISO' },
            { code: 'SC-BLOOD', name: 'Blood Specimen', description: 'Blood specimen collection', isActive: true, displayOrder: 1, categoryCode: 'SC' },
            { code: 'SC-CULTURE', name: 'Culture Specimen', description: 'Culture specimen collection', isActive: true, displayOrder: 2, categoryCode: 'SC' }
          ];
          for (const pt of protocolTypesToSeed) {
            await protocolTypeService.create(pt, userId);
          }
          console.log('✅ Protocol types seeded (no other data changed)');
          return {
            success: true,
            message: 'Protocol types added (existing data preserved)',
            counts: { protocolTypes: protocolTypesToSeed.length }
          };
        }
        console.log('❌ Initialization cancelled by user');
        return {
          success: false,
          message: 'Initialization cancelled - existing data preserved'
        };
      }
      didClear = true;
      
      // Delete all existing nomenclatures
      console.log('🗑️ Clearing existing nomenclatures...');
      const allCategories = await protocolCategoryService.getAll();
      const allProtocolTypes = await protocolTypeService.getAll();
      const allRoomTypes = await roomTypeService.getAll();
      const allPPETypes = await ppeTypeService.getAll();
      const allVerificationMethods = await verificationMethodService.getAll();
      const allTriggerEvents = await triggerEventService.getAll();
      const allFrequencyTypes = await frequencyTypeService.getAll();
      const allLocationTypes = await locationTypeService.getAll();
      const allProcessCategories = await processCategoryService.getAll();
      
      for (const item of allCategories) await protocolCategoryService.hardDelete(item.id);
      for (const item of allProtocolTypes) await protocolTypeService.hardDelete(item.id);
      for (const item of allRoomTypes) await roomTypeService.hardDelete(item.id);
      for (const item of allPPETypes) await ppeTypeService.hardDelete(item.id);
      for (const item of allVerificationMethods) await verificationMethodService.hardDelete(item.id);
      for (const item of allTriggerEvents) await triggerEventService.hardDelete(item.id);
      for (const item of allFrequencyTypes) await frequencyTypeService.hardDelete(item.id);
      for (const item of allLocationTypes) await locationTypeService.hardDelete(item.id);
      for (const item of allProcessCategories) await processCategoryService.hardDelete(item.id);
      
      console.log('✅ Existing data cleared');
    }
    
    // 1. Protocol Categories
    console.log('📋 Initializing Protocol Categories...');
    const categories = [
      { code: 'HYG', name: 'Hygiene', description: 'Hand hygiene and personal hygiene protocols', isActive: true, displayOrder: 1, icon: '🧼', color: '#3B82F6' },
      { code: 'DI', name: 'Device Insertion', description: 'Medical device insertion procedures', isActive: true, displayOrder: 2, icon: '💉', color: '#EF4444' },
      { code: 'DM', name: 'Device Maintenance', description: 'Medical device maintenance and care', isActive: true, displayOrder: 3, icon: '🔧', color: '#F59E0B' },
      { code: 'EC', name: 'Environmental Cleaning', description: 'Environmental cleaning and disinfection', isActive: true, displayOrder: 4, icon: '🧹', color: '#10B981' },
      { code: 'PC', name: 'Patient Care', description: 'General patient care protocols', isActive: true, displayOrder: 5, icon: '🏥', color: '#8B5CF6' },
      { code: 'MA', name: 'Medication Administration', description: 'Medication administration procedures', isActive: true, displayOrder: 6, icon: '💊', color: '#EC4899' },
      { code: 'SRG', name: 'Surgical', description: 'Surgical and perioperative protocols', isActive: true, displayOrder: 7, icon: '⚕️', color: '#6366F1' },
      { code: 'ISO', name: 'Isolation', description: 'Isolation and quarantine protocols', isActive: true, displayOrder: 8, icon: '🚪', color: '#DC2626' },
      { code: 'SC', name: 'Specimen Collection', description: 'Specimen collection procedures', isActive: true, displayOrder: 9, icon: '🧪', color: '#14B8A6' }
    ];
    for (const category of categories) {
      await protocolCategoryService.create(category, userId);
    }
    console.log('✅ Protocol Categories initialized');

    // 1.1. Protocol Types (one per category; categoryCode links to protocol_categories for form filtering)
    console.log('📋 Initializing Protocol Types...');
    const protocolTypes = [
      { code: 'HYG-HAND', name: 'Hand Hygiene', description: 'Hand hygiene and sanitization', isActive: true, displayOrder: 1, categoryCode: 'HYG' },
      { code: 'DI-LINE', name: 'Central Line Insertion', description: 'Central venous catheter insertion', isActive: true, displayOrder: 1, categoryCode: 'DI' },
      { code: 'DI-UC', name: 'Urinary Catheter Insertion', description: 'Urinary catheter insertion and care', isActive: true, displayOrder: 2, categoryCode: 'DI' },
      { code: 'DM-MAINT', name: 'Device Maintenance', description: 'Medical device maintenance and flushing', isActive: true, displayOrder: 1, categoryCode: 'DM' },
      { code: 'EC-ENV', name: 'Environmental Cleaning', description: 'Routine environmental cleaning and disinfection', isActive: true, displayOrder: 1, categoryCode: 'EC' },
      { code: 'EC-TERMINAL', name: 'Terminal Cleaning', description: 'Terminal/transfer cleaning', isActive: true, displayOrder: 2, categoryCode: 'EC' },
      { code: 'EC-WASTE', name: 'Waste Management', description: 'Waste segregation and disposal', isActive: true, displayOrder: 3, categoryCode: 'EC' },
      { code: 'PC-ADL', name: 'Activities of Daily Living', description: 'Patient ADL and mobility', isActive: true, displayOrder: 1, categoryCode: 'PC' },
      { code: 'PC-PPE', name: 'PPE Donning/Doffing', description: 'Personal protective equipment use', isActive: true, displayOrder: 2, categoryCode: 'PC' },
      { code: 'MA-IV', name: 'IV Medication', description: 'Intravenous medication administration', isActive: true, displayOrder: 1, categoryCode: 'MA' },
      { code: 'MA-ORAL', name: 'Oral Medication', description: 'Oral medication administration', isActive: true, displayOrder: 2, categoryCode: 'MA' },
      { code: 'SRG-PREOP', name: 'Preoperative', description: 'Preoperative preparation', isActive: true, displayOrder: 1, categoryCode: 'SRG' },
      { code: 'SRG-ASEPSIS', name: 'Surgical Asepsis', description: 'Surgical aseptic technique', isActive: true, displayOrder: 2, categoryCode: 'SRG' },
      { code: 'ISO-CONTACT', name: 'Contact Precautions', description: 'Contact precaution isolation', isActive: true, displayOrder: 1, categoryCode: 'ISO' },
      { code: 'ISO-DROPLET', name: 'Droplet Precautions', description: 'Droplet precaution isolation', isActive: true, displayOrder: 2, categoryCode: 'ISO' },
      { code: 'ISO-AIRBORNE', name: 'Airborne Precautions', description: 'Airborne precaution isolation', isActive: true, displayOrder: 3, categoryCode: 'ISO' },
      { code: 'SC-BLOOD', name: 'Blood Specimen', description: 'Blood specimen collection', isActive: true, displayOrder: 1, categoryCode: 'SC' },
      { code: 'SC-CULTURE', name: 'Culture Specimen', description: 'Culture specimen collection', isActive: true, displayOrder: 2, categoryCode: 'SC' }
    ];
    for (const pt of protocolTypes) {
      await protocolTypeService.create(pt, userId);
    }
    console.log('✅ Protocol Types initialized');

    // 1.2. Process Categories
    console.log('📋 Initializing Process Categories...');
    const processCategories = [
      { code: 'IPC', name: 'IPC Protocol', description: 'Infection Prevention & Control protocols', isActive: true, displayOrder: 1, icon: '🦠', color: '#DC2626' },
      { code: 'SAFETY', name: 'Safety & Risk Control', description: 'Safety and risk management processes', isActive: true, displayOrder: 2, icon: '🛡️', color: '#F59E0B' },
      { code: 'QUALITY', name: 'Quality Management', description: 'Quality assurance and improvement processes', isActive: true, displayOrder: 3, icon: '⭐', color: '#3B82F6' },
      { code: 'PATIENT', name: 'Patient Care', description: 'Direct patient care processes', isActive: true, displayOrder: 4, icon: '👤', color: '#10B981' },
      { code: 'ENV', name: 'Environmental Hygiene', description: 'Environmental cleaning and hygiene processes', isActive: true, displayOrder: 5, icon: '🧹', color: '#8B5CF6' },
      { code: 'EMERG', name: 'Emergency Response', description: 'Emergency and disaster response processes', isActive: true, displayOrder: 6, icon: '🚨', color: '#EF4444' },
      { code: 'TRAIN', name: 'Training & Education', description: 'Staff training and education processes', isActive: true, displayOrder: 7, icon: '📚', color: '#06B6D4' },
      { code: 'COMP', name: 'Compliance Monitoring', description: 'Compliance and audit processes', isActive: true, displayOrder: 8, icon: '📊', color: '#6366F1' },
      { code: 'EQUIP', name: 'Equipment Management', description: 'Medical equipment management processes', isActive: true, displayOrder: 9, icon: '⚙️', color: '#14B8A6' },
      { code: 'DOC', name: 'Documentation', description: 'Documentation and record-keeping processes', isActive: true, displayOrder: 10, icon: '📝', color: '#64748B' }
    ];
    for (const category of processCategories) {
      await processCategoryService.create(category, userId);
    }
    console.log('✅ Process Categories initialized');

    // 2. Room Types
    console.log('🚪 Initializing Room Types...');
    const roomTypes = [
      { code: 'ICU', name: 'ICU Room', description: 'Intensive Care Unit', isActive: true, displayOrder: 1, highRisk: true, requiresIsolation: false },
      { code: 'OR', name: 'Operating Room', description: 'Surgical operating room', isActive: true, displayOrder: 2, highRisk: true, requiresIsolation: false },
      { code: 'PR', name: 'Patient Room', description: 'General patient room', isActive: true, displayOrder: 3, highRisk: false, requiresIsolation: false },
      { code: 'ER', name: 'Emergency Room', description: 'Emergency department', isActive: true, displayOrder: 4, highRisk: true, requiresIsolation: false },
      { code: 'ISO', name: 'Isolation Room', description: 'Isolation room for infectious patients', isActive: true, displayOrder: 5, requiresIsolation: true, highRisk: true },
      { code: 'LAB', name: 'Laboratory', description: 'Laboratory room', isActive: true, displayOrder: 6, highRisk: false, requiresIsolation: false },
      { code: 'EXAM', name: 'Examination Room', description: 'Examination room', isActive: true, displayOrder: 7, highRisk: false, requiresIsolation: false },
      { code: 'PROCEDURE', name: 'Procedure Room', description: 'Medical procedure room', isActive: true, displayOrder: 8, highRisk: true, requiresIsolation: false },
      { code: 'RECOVERY', name: 'Recovery Room', description: 'Post-procedure recovery room', isActive: true, displayOrder: 9, highRisk: false, requiresIsolation: false }
    ];
    for (const roomType of roomTypes) {
      await roomTypeService.create(roomType, userId);
    }
    console.log('✅ Room Types initialized');

    // 3. PPE Types
    console.log('🧤 Initializing PPE Types...');
    const ppeTypes = [
      { code: 'GLOVES', name: 'Gloves', description: 'Disposable gloves', isActive: true, displayOrder: 1, category: 'Barrier', requiredForIsolation: true },
      { code: 'GOWN', name: 'Gown', description: 'Protective gown', isActive: true, displayOrder: 2, category: 'Barrier', requiredForIsolation: true },
      { code: 'MASK', name: 'Surgical Mask', description: 'Surgical mask', isActive: true, displayOrder: 3, category: 'Respiratory', requiredForIsolation: true },
      { code: 'N95', name: 'N95 Respirator', description: 'N95 respirator', isActive: true, displayOrder: 4, category: 'Respiratory', requiredForIsolation: true },
      { code: 'EYE', name: 'Eye Protection', description: 'Eye protection goggles', isActive: true, displayOrder: 5, category: 'Eye/Face', requiredForIsolation: false },
      { code: 'SHIELD', name: 'Face Shield', description: 'Face shield', isActive: true, displayOrder: 6, category: 'Eye/Face', requiredForIsolation: false },
      { code: 'APRON', name: 'Apron', description: 'Waterproof apron', isActive: true, displayOrder: 7, category: 'Barrier', requiredForIsolation: false },
      { code: 'BOOTS', name: 'Shoe Covers', description: 'Disposable shoe covers/boots', isActive: true, displayOrder: 8, category: 'Barrier', requiredForIsolation: false }
    ];
    for (const ppeType of ppeTypes) {
      await ppeTypeService.create(ppeType, userId);
    }
    console.log('✅ PPE Types initialized');

    // 4. Verification Methods
    console.log('✓ Initializing Verification Methods...');
    const verificationMethods = [
      { code: 'RFID', name: 'RFID Scan', description: 'RFID badge tap verification', isActive: true, displayOrder: 1, requiresDevice: true, automated: true },
      { code: 'BARCODE', name: 'Barcode Scan', description: 'Barcode scanning', isActive: true, displayOrder: 2, requiresDevice: true, automated: true },
      { code: 'QR', name: 'QR Code Scan', description: 'QR code scanning', isActive: true, displayOrder: 3, requiresDevice: true, automated: true },
      { code: 'VISUAL', name: 'Visual Confirmation', description: 'Observer visual confirmation', isActive: true, displayOrder: 4, requiresDevice: false, automated: false },
      { code: 'TIME', name: 'Time-based', description: 'Auto-confirm after duration', isActive: true, displayOrder: 5, requiresDevice: false, automated: true },
      { code: 'SENSOR', name: 'IoT Sensor', description: 'IoT sensor verification', isActive: true, displayOrder: 6, requiresDevice: true, automated: true },
      { code: 'PHOTO', name: 'Photo Evidence', description: 'Photo evidence', isActive: true, displayOrder: 7, requiresDevice: false, automated: false },
      { code: 'SIGNATURE', name: 'Digital Signature', description: 'Digital signature', isActive: true, displayOrder: 8, requiresDevice: false, automated: false },
      { code: 'CHECKLIST', name: 'Checklist', description: 'Checklist completion', isActive: true, displayOrder: 9, requiresDevice: false, automated: false }
    ];
    for (const method of verificationMethods) {
      await verificationMethodService.create(method, userId);
    }
    console.log('✅ Verification Methods initialized');

    // 5. Trigger Events
    console.log('🔔 Initializing Trigger Events...');
    const triggerEvents = [
      { code: 'PAT_ADM', name: 'Patient Admission', description: 'When a patient is admitted', isActive: true, displayOrder: 1, category: 'Patient', priority: 'High' },
      { code: 'PAT_TRF', name: 'Patient Transfer', description: 'When a patient is transferred', isActive: true, displayOrder: 2, category: 'Patient', priority: 'Medium' },
      { code: 'PAT_DIS', name: 'Patient Discharge', description: 'When a patient is discharged', isActive: true, displayOrder: 3, category: 'Patient', priority: 'Medium' },
      { code: 'DEV_INS', name: 'Device Insertion', description: 'When a medical device is inserted', isActive: true, displayOrder: 4, category: 'Device', priority: 'High' },
      { code: 'DEV_REM', name: 'Device Removal', description: 'When a medical device is removed', isActive: true, displayOrder: 5, category: 'Device', priority: 'Medium' },
      { code: 'PRE_PROC', name: 'Pre-procedure', description: 'Before a procedure', isActive: true, displayOrder: 6, category: 'Procedure', priority: 'High' },
      { code: 'POST_PROC', name: 'Post-procedure', description: 'After a procedure', isActive: true, displayOrder: 7, category: 'Procedure', priority: 'Medium' },
      { code: 'SHIFT_CHG', name: 'Shift Change', description: 'During shift handover', isActive: true, displayOrder: 8, category: 'System', priority: 'Low' },
      { code: 'OUTBREAK', name: 'Outbreak Declared', description: 'When an outbreak is declared', isActive: true, displayOrder: 9, category: 'System', priority: 'High' }
    ];
    for (const event of triggerEvents) {
      await triggerEventService.create(event, userId);
    }
    console.log('✅ Trigger Events initialized');

    // 6. Frequency Types
    console.log('📅 Initializing Frequency Types...');
    const frequencyTypes = [
      { code: 'SCHED', name: 'Scheduled', description: 'Protocol runs on a schedule', isActive: true, displayOrder: 1, requiresSchedule: true, requiresTrigger: false },
      { code: 'TRIG', name: 'Triggered', description: 'Protocol runs when triggered by event', isActive: true, displayOrder: 2, requiresSchedule: false, requiresTrigger: true },
      { code: 'CONT', name: 'Continuous', description: 'Protocol runs continuously', isActive: true, displayOrder: 3, requiresSchedule: false, requiresTrigger: false },
      { code: 'ONCE', name: 'Once', description: 'Protocol runs once', isActive: true, displayOrder: 4, requiresSchedule: false, requiresTrigger: false },
      { code: 'ON_DEMAND', name: 'On Demand', description: 'Protocol runs when manually initiated', isActive: true, displayOrder: 5, requiresSchedule: false, requiresTrigger: false }
    ];
    for (const frequencyType of frequencyTypes) {
      await frequencyTypeService.create(frequencyType, userId);
    }
    console.log('✅ Frequency Types initialized');

    // 7. Location Types (Clinical Structure Hierarchy)
    console.log('📍 Initializing Location Types (Clinical Structure)...');
    const locationTypes = [
      { code: 'ORG', name: 'Organization', description: 'Hospital/Healthcare organization', isActive: true, displayOrder: 1, level: 1 },
      { code: 'LOC', name: 'Location', description: 'Physical location/campus', isActive: true, displayOrder: 2, level: 2 },
      { code: 'BLD', name: 'Building', description: 'Building within location', isActive: true, displayOrder: 3, level: 3 },
      { code: 'FLR', name: 'Floor', description: 'Floor within building', isActive: true, displayOrder: 4, level: 4 },
      { code: 'DEPT', name: 'Department', description: 'Clinical department', isActive: true, displayOrder: 5, level: 5 },
      { code: 'ROOM', name: 'Room', description: 'Individual room', isActive: true, displayOrder: 6, level: 6 },
      { code: 'BED', name: 'Bed', description: 'Patient bed', isActive: true, displayOrder: 7, level: 7 },
      { code: 'DEV', name: 'Device', description: 'Medical device', isActive: true, displayOrder: 8, level: 8 }
    ];
    for (const locationType of locationTypes) {
      await locationTypeService.create(locationType, userId);
    }
    console.log('✅ Location Types initialized');

    console.log('');
    console.log('✅✅✅ ALL NOMENCLATURES INITIALIZED SUCCESSFULLY! ✅✅✅');
    console.log('');
    console.log('Summary:');
    console.log(`  - Protocol Categories: ${categories.length}`);
    console.log(`  - Protocol Types: ${protocolTypes.length}`);
    console.log(`  - Room Types: ${roomTypes.length}`);
    console.log(`  - PPE Types: ${ppeTypes.length}`);
    console.log(`  - Verification Methods: ${verificationMethods.length}`);
    console.log(`  - Trigger Events: ${triggerEvents.length}`);
    console.log(`  - Frequency Types: ${frequencyTypes.length}`);
    console.log(`  - Location Types: ${locationTypes.length}`);
    console.log('');

    return {
      success: true,
      counts: {
        protocolCategories: categories.length,
        protocolTypes: protocolTypes.length,
        roomTypes: roomTypes.length,
        ppeTypes: ppeTypes.length,
        verificationMethods: verificationMethods.length,
        triggerEvents: triggerEvents.length,
        frequencyTypes: frequencyTypes.length,
        locationTypes: locationTypes.length,
        processCategories: processCategories.length
      }
    };

  } catch (error) {
    console.error('❌ Error initializing nomenclatures:', error);
    throw error;
  }
}

// Export for use in other scripts or console
export { initializeNomenclatures };

// If run directly (for testing in console)
if (typeof window !== 'undefined') {
  window.initializeNomenclatures = initializeNomenclatures;
}

